import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import { User } from "../types";
import { apiClient } from "../utils/apiClient";

const UserMaintenance = () => {
  const API_PREFIX = "/api";

  const [users, setUsers] = useState<User[]>([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // フィルタ用ステート
  const [filterRole, setFilterRole] = useState<string>("all");
  const [searchName, setSearchName] = useState("");
  const [searchEmail, setSearchEmail] = useState("");

  // 行内編集用ステート
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editIsAdmin, setEditIsAdmin] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = () => {
    apiClient
      .get<User[]>(`${API_PREFIX}/users`)
      .then((data) => setUsers(data))
      .catch((err) => setErrorMessage("ユーザー情報の取得に失敗しました"));
  };

  const resetMessages = () => {
    setSuccessMessage("");
    setErrorMessage("");
  };

  // --- フィルタリングロジック ---
  const filteredUsers = users.filter((u) => {
    // 権限フィルタ
    const matchesRole =
      filterRole === "all"
        ? true
        : filterRole === "admin"
          ? u.is_admin
          : !u.is_admin;

    // 名前検索（部分一致・大文字小文字無視）
    const matchesName = u.name.toLowerCase().includes(searchName.toLowerCase());

    // メールアドレス検索（部分一致・大文字小文字無視）
    const matchesEmail = u.email
      .toLowerCase()
      .includes(searchEmail.toLowerCase());

    return matchesRole && matchesName && matchesEmail;
  });

  // --- 行内編集コントロール ---
  const startEdit = (user: User) => {
    resetMessages();
    setEditingId(user.id);
    setEditName(user.name);
    setEditIsAdmin(user.is_admin);
  };

  const cancelEdit = () => {
    setEditingId(null);
    resetMessages();
  };

  const handleUpdate = async (id: number) => {
    resetMessages();
    const updateData = { name: editName, is_admin: editIsAdmin };
    try {
      await apiClient.put(`${API_PREFIX}/users/${id}`, { user: updateData });
      setEditingId(null);
      setSuccessMessage("更新しました");
      fetchUsers();
    } catch (err: any) {
      setErrorMessage(err.message || "更新に失敗しました");
    }
  };

  return (
    <div
      className="container-fluid p-0"
      style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}
    >
      <Header title="ユーザー管理" />

      <div className="limit-width px-4">
        <div className="mb-4">
          <Link
            to="/admin"
            className="text-decoration-none text-secondary d-inline-flex align-items-center"
          >
            <span className="fs-4 me-2">‹</span> 管理者メニューに戻る
          </Link>
        </div>

        {successMessage && (
          <div className="alert alert-success mb-4 border-0 shadow-sm">
            ✅ {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="alert alert-danger mb-4 border-0 shadow-sm">
            ⚠️ {errorMessage}
          </div>
        )}

        {/* --- フィルタリングUI --- */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-body bg-white rounded">
            <div className="row g-3 align-items-center">
              <div className="col-auto">
                <span className="small fw-bold text-secondary">絞り込み:</span>
              </div>
              <div className="col-md-2">
                <select
                  className="form-select form-select-sm"
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                >
                  <option value="all">すべての権限</option>
                  <option value="admin">管理者のみ</option>
                  <option value="general">一般のみ</option>
                </select>
              </div>
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="名前で検索..."
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="メールアドレスで検索..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                />
              </div>
              <div className="col text-end text-secondary small">
                該当件数: <strong>{filteredUsers.length}</strong> 件
              </div>
            </div>
          </div>
        </div>

        {/* ユーザー一覧テーブル */}
        <div className="card shadow-sm border-0 overflow-hidden">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th style={{ width: "10%" }} className="ps-4">
                    ID
                  </th>
                  <th style={{ width: "15%" }}>権限</th>
                  <th style={{ width: "25%" }}>名前</th>
                  <th style={{ width: "35%" }}>メールアドレス</th>
                  <th style={{ width: "15%" }} className="text-center">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    {editingId === user.id ? (
                      <>
                        <td className="ps-4 text-secondary fw-bold">
                          {user.id}
                        </td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={editIsAdmin}
                              onChange={(e) => setEditIsAdmin(e.target.checked)}
                            />
                            <span
                              className={`badge ${editIsAdmin ? "bg-danger" : "bg-light text-dark border"}`}
                            >
                              {editIsAdmin ? "管理者" : "一般"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm border-primary"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            autoFocus
                          />
                        </td>
                        <td className="text-muted small">{user.email}</td>
                        <td className="text-center pe-3 text-nowrap">
                          <button
                            className="btn btn-success btn-sm me-2 shadow-sm"
                            onClick={() => handleUpdate(user.id)}
                          >
                            保存
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={cancelEdit}
                          >
                            中止
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="ps-4 text-secondary">{user.id}</td>
                        <td>
                          <span
                            className={`badge ${user.is_admin ? "bg-danger" : "bg-light text-dark border"}`}
                          >
                            {user.is_admin ? "管理者" : "一般"}
                          </span>
                        </td>
                        <td className="fw-bold text-dark">{user.name}</td>
                        <td className="text-secondary">{user.email}</td>
                        <td className="text-center pe-3">
                          <button
                            className="btn btn-sm btn-outline-primary px-3 shadow-sm"
                            onClick={() => startEdit(user)}
                          >
                            編集
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">
                      一致するユーザーが見つかりません
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserMaintenance;
